import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { Grid, Field } from 'nallian-shared-ui/lib/components';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { FieldArray } from 'redux-form';
import {
  InputFieldValueComponent,
  LookupFieldValueComponent,
  CheckBoxFieldValueComponent,
} from 'nallian-shared-ui/lib/components/redux-form-semantic-ui';
import { FormModal } from 'nallian-shared-ui/lib/modules';
import { GetRights } from 'nallian-shared-ui/lib/selectors';
import { hasRight } from 'nallian-shared-ui/lib/lib/routesHelper';
import { FORM_NAME } from '../constants';
import OwnerFields from './owners/OwnerFields';
import { generateErrorsForRequiredFields } from '../../app/lib/validationHelper';
import { normalizeTag } from '../../app/helpers/normalizeTag';
import { TagExplainer } from '../../app/styles';
import organisationsService from '../../organisations/services/organisationsService';
import getCommunitiesService from '../../organisations/services/getCommunities';
import ReadOnlyfieldValueComponent from '../../app/components/ReadOnlyFieldValueComponent';
import apiRoutes from '../../../app/api/routes';

const REQUIRED_FIELDS = ['name', 'tag'];

const validate = values => {
  let errors = {};
  errors = generateErrorsForRequiredFields(REQUIRED_FIELDS, values, errors);

  return errors;
};

const TypeForm = ({
  submitProcessing,
  dataLoading,
  data,
  returnRoute,
  onSubmit,
  header,
  editMode,
  getOrganisationsForSelectOptions,
  dispatchOrganisationsGet,
  communitiesFetch,
}) => {
  const { t } = useTranslation();

  const organisations = getOrganisationsForSelectOptions();

  const rights = useSelector(GetRights);

  const readOnly = useMemo(() => !hasRight(rights, 'identification-code-types/create'), [rights]);

  useEffect(dispatchOrganisationsGet, [dispatchOrganisationsGet]);

  const communities = useMemo(
    () =>
      (communitiesFetch.value || []).map(community => ({
        key: community.name,
        value: community.id,
        text: community.name,
      })),
    [communitiesFetch.value]
  );

  return (
    <FormModal
      closeIcon
      header={header}
      dataLoading={dataLoading}
      submitProcessing={submitProcessing}
      onSubmit={onSubmit}
      data={data}
      t={t}
      validate={validate}
      form={FORM_NAME}
      cancelRoute={returnRoute}
      saveButtonIcon="save"
      saveButtonContent={t('save')}
      cancelButtonContent={readOnly ? t('close') : t('cancel')}
      noSaveButton={readOnly}
    >
      <Grid>
        <Grid.Column mobile={8} tablet={8} computer={8}>
          <Field
            fluid
            label={t('name')}
            name="name"
            valueComponent={readOnly ? ReadOnlyfieldValueComponent : InputFieldValueComponent}
            disabled={submitProcessing || dataLoading}
          />
        </Grid.Column>
        <Grid.Column mobile={8} tablet={8} computer={8}>
          <Field
            fluid
            label={
              <span>
                {t('tag')}
                &nbsp;
                <TagExplainer>({t('tagExplainer')})</TagExplainer>
              </span>
            }
            name="tag"
            normalize={normalizeTag}
            valueComponent={readOnly ? ReadOnlyfieldValueComponent : InputFieldValueComponent}
            disabled={submitProcessing || dataLoading || editMode}
          />
        </Grid.Column>
        <Grid.Column mobile={8} tablet={8} computer={8}>
          <Field
            fluid
            search
            clearable
            label={t('validationPattern')}
            name="validationPatternId"
            valueComponent={readOnly ? ReadOnlyfieldValueComponent : LookupFieldValueComponent}
            url={apiRoutes.validationPatterns()}
            disabled={submitProcessing || dataLoading}
          />
        </Grid.Column>
        <Grid.Column mobile={8} tablet={8} computer={8}>
          <Field
            fluid
            toggle
            label={t('isHidden')}
            name="isHidden"
            valueComponent={readOnly ? ReadOnlyfieldValueComponent : CheckBoxFieldValueComponent}
            disabled={submitProcessing || dataLoading}
          />
        </Grid.Column>
        <Grid.Column mobile={16} tablet={16} computer={16}>
          <FieldArray
            organisations={organisations}
            communities={communities}
            name="owners"
            component={OwnerFields}
            submitProcessing={submitProcessing}
            readOnly={readOnly}
          />
        </Grid.Column>
      </Grid>
    </FormModal>
  );
};

TypeForm.propTypes = {
  dispatchOrganisationsGet: PropTypes.func.isRequired,
  organisationsFetch: PropTypes.object.isRequired,
  communitiesFetch: PropTypes.object.isRequired,
  getOrganisationsForSelectOptions: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  dataLoading: PropTypes.bool,
  submitProcessing: PropTypes.bool,
  header: PropTypes.string.isRequired,
  returnRoute: PropTypes.string.isRequired,
  data: PropTypes.object,
  editMode: PropTypes.bool,
};

TypeForm.defaultProps = {
  dataLoading: false,
  submitProcessing: false,
  data: {},
  editMode: false,
};

export default compose(getCommunitiesService, organisationsService)(TypeForm);
